import React, { HTMLProps, useState } from 'react';
import { IconElements } from '../../config/icons';
import { Tooltip } from './Tooltip';
import { TooltipIndicator } from './TooltipIndicator';

const SectionHeader: React.FC<{ title: string; isIconUp: boolean }> = ({ title, isIconUp }) => (
    <div className={`flex flex-row justify-between items-center bg-white shadow-xl-all-sides rounded-md p-2 px-4 `}>
        <span>{title}</span>
        <span className="">
            <IconElements.ChevronDown
                className={`text-bnd-green h-4 w-4 transition ${isIconUp ? '' : 'transform rotate-180'}`}
            />
        </span>
    </div>
);

const ResultOptionHeader: React.FC<{
    title: string;
    isIconUp: boolean;
    isCompleted: boolean;
    tooltip?: {
        title?: string;
        text: string;
    };
}> = ({ title, isIconUp, isCompleted, tooltip }) => (
    <div className={`flex flex-row justify-between items-center rounded-md my-2 mx-4`}>
        <span className={`flex-1 flex justify-between mr-4 items-center ${!tooltip && 'pt-1 pb-2'}`}>
            <div className="flex flex-row gap-2 sm:gap-4 items-center">
                {isCompleted && (
                    <div className="flex-none">
                        <IconElements.PensionSectionDoneIcon />
                    </div>
                )}
                <span className="font-medium text-lg">{title}</span>
            </div>
            {!!tooltip && (
                <Tooltip tooltipTitle={tooltip.title} tooltipText={tooltip.text}>
                    <TooltipIndicator>?</TooltipIndicator>
                </Tooltip>
            )}
        </span>
        <span className="">
            <IconElements.ChevronDown
                className={`text-bnd-green h-4 w-4 transition ${isIconUp ? '' : 'transform rotate-180'}`}
            />
        </span>
    </div>
);

export const Disclosure: React.FC<
    {
        DisclosureHeader: (isOpen: boolean) => React.ReactNode;
        hiddenState?: boolean;
        setHiddenState?: (hiddenState: boolean) => void;
    } & HTMLProps<HTMLDivElement>
> = ({ children, DisclosureHeader, className, hiddenState, setHiddenState, ...rest }) => {
    const [hiddenStateLocal, setHiddenStateLocal] = useState(true);

    return (
        <div {...rest} className={`flex flex-col w-full ${className}`}>
            <div
                className=""
                onClick={() => {
                    setHiddenState === undefined || hiddenState === undefined
                        ? setHiddenStateLocal(!hiddenStateLocal)
                        : setHiddenState(!hiddenState);
                }}
            >
                {DisclosureHeader(
                    setHiddenState === undefined || hiddenState === undefined ? hiddenStateLocal : hiddenState,
                )}
            </div>
            <div
                className={` ${
                    (setHiddenState === undefined || hiddenState === undefined ? hiddenStateLocal : hiddenState)
                        ? 'hidden'
                        : ''
                }`}
            >
                {children}
            </div>
        </div>
    );
};

export const SectionDisclosure: React.FC<
    {
        title: string;
        hiddenState?: boolean;
        setHiddenState?: (hiddenState: boolean) => void;
    } & HTMLProps<HTMLDivElement>
> = ({ title, children, hiddenState, setHiddenState, ...rest }) => {
    return (
        <Disclosure
            {...rest}
            DisclosureHeader={(hideChildren) => (
                <div className={`px-4 bg-white cursor-pointer lg:bg-white ${hideChildren ? 'pb-3 pt-0.5' : 'py-0.5'}`}>
                    <SectionHeader title={title} isIconUp={hideChildren} />
                </div>
            )}
            hiddenState={hiddenState}
            setHiddenState={setHiddenState}
        >
            {children}
        </Disclosure>
    );
};

export const ResultOptionDisclosure: React.FC<
    {
        title: string;
        isCompleted?: boolean;
        tooltip?: {
            title?: string;
            text: string;
        };
        hiddenState?: boolean;
        setHiddenState?: (hiddenState: boolean) => void;
    } & HTMLProps<HTMLDivElement>
> = ({ title, isCompleted = false, children, tooltip, hiddenState, setHiddenState, ...rest }) => {
    return (
        <Disclosure
            {...rest}
            DisclosureHeader={(hideChildren) => (
                <div className={`cursor-pointer pt-2 ${hideChildren ? 'pb-2 border-b' : ''}`}>
                    <ResultOptionHeader
                        title={title}
                        isIconUp={hideChildren}
                        isCompleted={isCompleted}
                        tooltip={tooltip}
                    />
                </div>
            )}
            hiddenState={hiddenState}
            setHiddenState={setHiddenState}
        >
            {children}
        </Disclosure>
    );
};

export const StickyDisclosure: React.FC<{
    disclosureTitle: string;
    hiddenState?: boolean;
    setHiddenState?: (hiddenState: boolean) => void;
    children?: React.ReactNode;
}> = ({ disclosureTitle, children, hiddenState, setHiddenState }) => {
    return (
        <Disclosure
            DisclosureHeader={(hideChildren) => (
                <div className="text-lg py-2 font-medium cursor-pointer">
                    <SectionHeader title={disclosureTitle} isIconUp={hideChildren} />
                </div>
            )}
            hiddenState={hiddenState}
            setHiddenState={setHiddenState}
        >
            {children}
        </Disclosure>
    );
};
